/**
 * @file: EntityCardPickerPanel.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * The picker body shared by desktop aside + mobile sheet: a search box and
 * an infinite list of TaskCardV2 cards. Order/service-record resolve on
 * tap; report taps open the ReportTypeChooser sub-step.
 */

import {
  TaskCardV2,
  useTaskCardV2Metrics,
} from '@euler/app/components/task-card';
import { Typography } from '@euler/app/components/typography';
import { EmptyView } from '@euler/components/EmptyView';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TaskListItem } from '@euler/model/TaskListItem';
import { onErrorIgnore } from '@euler/utils';
import { Search, X } from 'lucide-react-native';
import { FC, useCallback, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { ReportTypeChooser } from './ReportTypeChooser';
import { EntityCardKind, EntityCardPickerResult } from './types';
import { useEntityCardList } from './useEntityCardList';

const TITLES: Record<EntityCardKind, string> = {
  order: '选择工单',
  'service-record': '选择服务记录',
  report: '选择报告',
};

const PLACEHOLDERS: Record<EntityCardKind, string> = {
  order: '搜索车牌号 / 关键词',
  'service-record': '搜索车牌号 / 关键词',
  report: '搜索车牌号 / 关键词',
};

const keyExtractor = (item: TaskListItem) => item.id;

export const EntityCardPickerPanel: FC<{
  kind: EntityCardKind;
  onSelect: (result: EntityCardPickerResult) => void;
  onClose: () => void;
}> = ({ kind, onSelect, onClose }) => {
  const { rpx } = useResponsiveContext();
  const { height: cardHeight } = useTaskCardV2Metrics();
  const [search, setSearch] = useState('');
  const [width, setWidth] = useState(0);
  // report flow: an order is selected, awaiting report-type choice.
  const [reportTarget, setReportTarget] = useState<TaskListItem | null>(null);

  const { data, status, fetchMore } = useEntityCardList(kind, search);

  const onItemPress = useCallback(
    (item: TaskListItem) => {
      if (kind === 'order') onSelect({ kind: 'order', item });
      else if (kind === 'service-record') {
        onSelect({ kind: 'service-record', item });
      } else setReportTarget(item);
    },
    [kind, onSelect],
  );

  const onEndReached = useCallback(() => {
    fetchMore().catch(onErrorIgnore);
  }, [fetchMore]);

  const renderItem: ListRenderItem<TaskListItem> = useCallback(
    ({ item }) => (
      <TaskCardV2
        item={item}
        onPress={onItemPress}
        style={{
          width: width > 0 ? width - rpx(24) : undefined,
          height: cardHeight,
          marginHorizontal: rpx(12),
          marginBottom: rpx(10),
        }}
      />
    ),
    [onItemPress, width, cardHeight, rpx],
  );

  if (reportTarget) {
    return (
      <ReportTypeChooser
        item={reportTarget}
        onBack={() => setReportTarget(null)}
        onChoose={reportType =>
          onSelect({ kind: 'report', item: reportTarget, reportType })
        }
      />
    );
  }

  const loading = status.isLoading && data.length === 0;

  return (
    <View
      style={{ flex: 1 }}
      onLayout={e => {
        const w = e.nativeEvent.layout.width;
        if (w !== width) setWidth(w);
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(14),
        }}
      >
        <Typography.Text
          size={rpx(16)}
          color="#111"
          style={{ fontFamily: FontFamily.NotoSans.Bold }}
        >
          {TITLES[kind]}
        </Typography.Text>
        <TouchableOpacity
          onPress={onClose}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <X size={rpx(22)} color="#111" />
        </TouchableOpacity>
      </View>

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(8),
          marginHorizontal: rpx(12),
          marginBottom: rpx(8),
          paddingHorizontal: rpx(12),
          height: rpx(40),
          borderRadius: rpx(20),
          backgroundColor: '#F1F5F9',
          borderWidth: 1,
          borderColor: '#E2E8F0',
        }}
      >
        <Search size={rpx(18)} color="#94A3B8" />
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder={PLACEHOLDERS[kind]}
          placeholderTextColor="#94A3B8"
          style={{
            flex: 1,
            fontSize: rpx(14),
            color: '#1E293B',
            fontFamily: FontFamily.NotoSans.Regular,
            paddingVertical: 0,
          }}
        />
      </View>

      {loading ? (
        <View
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        >
          <ActivityIndicator />
        </View>
      ) : (
        <FlatList
          data={data}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.3}
          contentContainerStyle={{ paddingTop: rpx(4), paddingBottom: rpx(24) }}
          ListEmptyComponent={
            <View style={{ paddingTop: rpx(40) }}>
              <EmptyView message="没有找到结果" />
            </View>
          }
          ListFooterComponent={
            status.isLoading && data.length > 0 ? (
              <View style={{ paddingVertical: rpx(16) }}>
                <ActivityIndicator />
              </View>
            ) : null
          }
        />
      )}
    </View>
  );
};
